/**
 * Short start-time and place text for a task (#2705). A timed task reads
 * "16:51 · Stage" on the board card, the outline row and the Map Home list.
 * The time is the team's wall clock, 24 hours (#2721). Without a team zone it
 * is the viewer's own.
 */

function zoneOpt(timeZone: string | undefined): { timeZone?: string } {
  if (!timeZone) return {};
  try {
    new Intl.DateTimeFormat(undefined, { timeZone });
    return { timeZone };
  } catch {
    return {};
  }
}

/** True when the viewer's browser zone differs from the team zone. */
export function zoneDiffers(timeZone: string | undefined): boolean {
  if (!timeZone || !zoneOpt(timeZone).timeZone) return false;
  return Intl.DateTimeFormat().resolvedOptions().timeZone !== timeZone;
}

/** Wall-clock parts of an instant in a zone (the viewer's zone when none). */
function wallParts(ms: number, timeZone: string | undefined) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
    ...zoneOpt(timeZone),
  }).formatToParts(new Date(ms));
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  return {
    year: get("year"),
    month: get("month"),
    day: get("day"),
    hour: get("hour"),
    minute: get("minute"),
    second: get("second"),
  };
}

/** Offset in ms of the zone at an instant: wall clock read as UTC minus the instant. */
function zoneOffsetMs(ms: number, timeZone: string | undefined): number {
  const w = wallParts(ms, timeZone);
  const asUtc = Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second);
  return asUtc - Math.floor(ms / 1000) * 1000;
}

/** ISO instant to the `YYYY-MM-DDTHH:mm` a datetime-local input wants, in the zone. */
export function isoToZonedInput(iso: string | null | undefined, timeZone?: string): string {
  if (!iso) return "";
  const ms = new Date(iso).getTime();
  if (Number.isNaN(ms)) return "";
  const w = wallParts(ms, timeZone);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${w.year}-${p(w.month)}-${p(w.day)}T${p(w.hour)}:${p(w.minute)}`;
}

/** A datetime-local value read as wall clock in the zone, back to an ISO instant. */
export function zonedInputToIso(value: string, timeZone?: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value);
  if (!m) return new Date(value).toISOString();
  const wall = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]));
  let ms = wall - zoneOffsetMs(wall, timeZone);
  ms = wall - zoneOffsetMs(ms, timeZone);
  return new Date(ms).toISOString();
}

export function timeOfDay(iso: string | null | undefined, timeZone?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    ...zoneOpt(timeZone),
  });
}

/** The app's own date format, the same one the Target date row uses. */
export function appDate(iso: string | null | undefined, timeZone?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    ...zoneOpt(timeZone),
  });
}

/** "16:51 · Stage", "16:51", "Stage" or "" when the task has neither. */
export function timingMeta(
  node: {
    startsAt?: string | null | undefined;
    place?: string | null | undefined;
  },
  timeZone?: string,
): string {
  return [timeOfDay(node.startsAt, timeZone), node.place?.trim() ?? ""].filter(Boolean).join(" · ");
}
